import FormControlLabel from '@mui/material/FormControlLabel';
import { Checkbox } from 'components/Checkbox/Checkbox';
import { CollapsableFormZone } from 'components/Form/CollapsableFormZone/CollapsableFormZone';
import { NumericInput } from 'components/Inputs/NumericInput/NumericInput';
import type { JSX } from 'react';
import { PAYMENT_METHODS, PaymentMethod } from 'services/api/legalCorporationConfig/types';

import { AllowedPaymentMethodsLabel, PaymentMethodRow } from '../SettingsForm.style';
import {
  AVAILABLE_PAYMENT_METHODS,
  IS_PAYMENT_METHOD_SELECTABLE,
  PAYMENT_METHOD_LIMIT_KEYS,
  SettingsFormKeys,
} from '../settingsFormValues';
import { SettingsSectionProps, useSettingsFieldHelpers } from '../useSettingsFieldHelpers';

export const PaymentConfigurationSection = ({
  formik,
  isReadOnly,
}: SettingsSectionProps): JSX.Element => {
  const { settingsFormIntl, getTextFieldProps } = useSettingsFieldHelpers(formik, isReadOnly);

  const togglePaymentMethod = (method: PaymentMethod, isChecked: boolean) => {
    const allowedPaymentMethods = formik.values[SettingsFormKeys.ALLOWED_PAYMENT_METHODS];
    formik.setFieldValue(
      SettingsFormKeys.ALLOWED_PAYMENT_METHODS,
      PAYMENT_METHODS.filter((paymentMethod) =>
        paymentMethod === method ? isChecked : allowedPaymentMethods.includes(paymentMethod),
      ),
    );
  };

  return (
    <CollapsableFormZone title={settingsFormIntl('sections.payment-configuration')}>
      <>
        <AllowedPaymentMethodsLabel>
          {settingsFormIntl('form.allowed-payment-methods.label')}
        </AllowedPaymentMethodsLabel>
        {AVAILABLE_PAYMENT_METHODS.map((method) => {
          const limitKey = PAYMENT_METHOD_LIMIT_KEYS[method];
          const isAllowed =
            formik.values[SettingsFormKeys.ALLOWED_PAYMENT_METHODS].includes(method);
          return (
            <PaymentMethodRow key={method}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isAllowed}
                    onChange={(event) => togglePaymentMethod(method, event.target.checked)}
                    disabled={isReadOnly || !IS_PAYMENT_METHOD_SELECTABLE}
                  />
                }
                label={settingsFormIntl(`form.payment-methods.${method}`)}
              />
              <NumericInput
                {...getTextFieldProps(limitKey, 'payment-limit')}
                label={settingsFormIntl('form.payment-limit.label', {
                  paymentMethod: settingsFormIntl(`form.payment-methods.${method}`),
                })}
                inputProps={{ min: 0, step: 0.01 }}
                disabled={isReadOnly || !isAllowed}
                required={isAllowed}
              />
            </PaymentMethodRow>
          );
        })}
      </>
    </CollapsableFormZone>
  );
};
